import { useState, useEffect, useCallback } from 'react';

const ARTIFACT_PANEL_WIDTH_STORAGE_KEY = 'artifactPanelWidth';
const DEFAULT_WIDTH = 755;
const MIN_WIDTH = 755;
const MIN_CHAT_WIDTH = 700; // Minimum width for chat area + buffer for sidebar (400 + ~300 for sidebar)

export const useArtifactPanelWidth = () => {
  const [panelWidth, setPanelWidth] = useState<number>(() => {
    const savedWidth = localStorage.getItem(ARTIFACT_PANEL_WIDTH_STORAGE_KEY);
    if (savedWidth) {
      const width = parseInt(savedWidth, 10);
      return Math.max(MIN_WIDTH, width);
    }
    return DEFAULT_WIDTH;
  });

  const getMaxWidth = useCallback(() => {
    const windowWidth = window.innerWidth;
    return Math.max(MIN_WIDTH, windowWidth - MIN_CHAT_WIDTH);
  }, []);

  const updatePanelWidth = useCallback((newWidth: number) => {
    const currentMaxWidth = Math.max(MIN_WIDTH, window.innerWidth - MIN_CHAT_WIDTH);
    const constrainedWidth = Math.max(MIN_WIDTH, Math.min(currentMaxWidth, newWidth));
    setPanelWidth(constrainedWidth);

    localStorage.setItem(ARTIFACT_PANEL_WIDTH_STORAGE_KEY, constrainedWidth.toString());
  }, []);

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === ARTIFACT_PANEL_WIDTH_STORAGE_KEY && e.newValue) {
        const width = parseInt(e.newValue, 10);
        if (!isNaN(width)) {
          const maxWidth = getMaxWidth();
          setPanelWidth(Math.max(MIN_WIDTH, Math.min(maxWidth, width)));
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [getMaxWidth]);

  useEffect(() => {
    const handleResize = () => {
      const maxWidth = getMaxWidth();
      if (panelWidth > maxWidth) {
        updatePanelWidth(maxWidth);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [panelWidth, getMaxWidth, updatePanelWidth]);

  return {
    panelWidth,
    updatePanelWidth,
    minWidth: MIN_WIDTH,
    maxWidth: Math.max(MIN_WIDTH, window.innerWidth - MIN_CHAT_WIDTH),
  };
};
